'use client';

import CommentIcon from '@/assets/svgs/comment.svg';
import EnvelopeIcon from '@/assets/svgs/envelope.svg';
import KeyboardIcon from '@/assets/svgs/keyboard.svg';
import CanvassersIconSvg from '@/assets/svgs/main/canvassers-filled.svg';
import JobsIcon from '@/assets/svgs/main/jobs.svg';
import PlusIcon from '@/assets/svgs/plus-large.svg';
import StatusSvg from '@/assets/svgs/status.svg';
import ActiveIcon from '@/assets/svgs/statuses/active.svg';
import AwaitingIcon from '@/assets/svgs/statuses/awaiting.svg';
import CompleteIcon from '@/assets/svgs/statuses/complete.svg';
import DeferredIcon from '@/assets/svgs/statuses/deferred.svg';
import PausedIcon from '@/assets/svgs/statuses/paused.svg';
import PendingIcon from '@/assets/svgs/statuses/pending.svg';
import TrashIcon from '@/assets/svgs/trash-can.svg';
import { inter500 } from '@/config/fonts';
import {
  useFetchCanvasserDetails,
  useResetCanvasserPassword,
} from '@/containers/Canvassers/api';
import { Modals } from '@/containers/Modals/all-modals';
import ModalContainer from '@/containers/Modals/ModalContainer';
import ModalHeader from '@/containers/Modals/ModalHeader';
import { addOpenModal } from '@/containers/Modals/redux/modals.slice';
import useModals from '@/containers/Modals/useModals';
import { useToast } from '@/containers/ToastMessage';
import { useAppDispatch } from '@/lib/redux/store';
import { BulkActionButton, ModalActionsContainer } from '@/shared/components/BulkActions';
import DetailsCard from '@/shared/components/DetailsCard';
import { formatRelativeTime } from '@/shared/utils/formatRelativeTime';
import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ModalProps } from '../types';

const modalName = Modals.CanvasserDetailsModal;

// Get status icon based on canvasser status
const getStatusIcon = (status?: string) => {
  switch (status) {
    case 'active':
      return <ActiveIcon className="h-4 w-4" />;
    case 'paused':
      return <PausedIcon className="h-4 w-4" />;
    default:
      return null;
  }
};

// Get status icon based on job status
const getJobStatusIcon = (status?: string) => {
  switch (status) {
    case 'active':
      return <ActiveIcon className="h-4 w-4" />;
    case 'paused':
      return <PausedIcon className="h-4 w-4" />;
    case 'pending':
      return <PendingIcon className="h-4 w-4" />;
    case 'complete':
      return <CompleteIcon className="h-4 w-4" />;
    case 'deferred':
      return <DeferredIcon className="h-4 w-4" />;
    case 'awaiting':
    case 'awaiting-check':
      return <AwaitingIcon className="h-4 w-4" />;
    default:
      return null;
  }
};

interface CanvasserDetailsModalProps extends ModalProps {
  className?: string;
  params?: { canvasserId?: string };
}

const CanvasserDetailsModal = ({
  className,
  params,
  variant = 'narrow',
}: CanvasserDetailsModalProps) => {
  const { showSuccess, showError } = useToast();
  const dispatch = useAppDispatch();

  // State for job selections
  const [selectedJobs, setSelectedJobs] = useState<Set<string>>(new Set());
  const [selectAllJobs, setSelectAllJobs] = useState(false);

  const { isModalOpen, onClose } = useModals({
    modalName,
    shouldCloseOnEscape: true,
  });

  // Get canvasserId from modalParams like ContactListDetailsModal does
  const { canvasserId } = params || {};

  const {
    data: response,
    isLoading,
    isError,
  } = useFetchCanvasserDetails(canvasserId || undefined);

  const resetPasswordMutation = useResetCanvasserPassword();

  const canvasser = response?.data;

  // Clear job selections when modal closes or canvasser changes
  useEffect(() => {
    if (!isModalOpen) {
      setSelectedJobs(new Set());
      setSelectAllJobs(false);
    }
  }, [isModalOpen]);

  useEffect(() => {
    setSelectedJobs(new Set());
    setSelectAllJobs(false);
  }, [canvasser?.id]);

  const handleResetPassword = () => {
    if (!canvasser) return;

    const newPassword = prompt('Enter new password for canvasser:');
    if (!newPassword) return;

    resetPasswordMutation.mutate(
      { canvasser_id: canvasser.id, password: newPassword },
      {
        onSuccess: () => {
          showSuccess('Password reset successfully');
        },
        onError: () => {
          showError('Failed to reset password');
        },
      }
    );
  };

  const handleDeleteCanvasser = () => {
    if (!canvasser) return;

    const confirmed = window.confirm(
      `Are you sure you want to delete canvasser "${canvasser.display_name}"? This action cannot be undone.`
    );

    if (confirmed) {
      // TODO: Implement delete canvasser API
      showSuccess('Canvasser deleted successfully');
      onClose();
    }
  };

  const handleAssignToJob = () => {
    if (!canvasser) return;

    const assignedJobIds = canvasser.jobs_details?.map((job) => job.id) || [];

    dispatch(
      addOpenModal({
        modalName: Modals.AssignJobsModal,
        modalParams: JSON.stringify({
          canvasserId: canvasser.id,
          assignedJobIds,
        }),
      })
    );
  };

  // Job selection handlers
  const handleSelectJob = useCallback(
    (jobId: string, selected: boolean) => {
      setSelectedJobs((prev) => {
        const newSet = new Set(prev);
        if (selected) {
          newSet.add(jobId);
        } else {
          newSet.delete(jobId);
        }

        // Update select all state
        const totalJobs = canvasser?.jobs_details?.length || 0;
        setSelectAllJobs(newSet.size === totalJobs && totalJobs > 0);

        return newSet;
      });
    },
    [canvasser?.jobs_details?.length]
  );

  const handleSelectAllJobs = useCallback(
    (selected: boolean) => {
      setSelectAllJobs(selected);
      if (selected && canvasser?.jobs_details) {
        setSelectedJobs(new Set(canvasser.jobs_details.map((job) => job.id)));
      } else {
        setSelectedJobs(new Set());
      }
    },
    [canvasser?.jobs_details]
  );

  // Determine if select all checkbox should be indeterminate
  const isSelectAllJobsIndeterminate = useMemo(() => {
    const selectedCount = selectedJobs.size;
    const totalCount = canvasser?.jobs_details?.length || 0;
    return selectedCount > 0 && selectedCount < totalCount;
  }, [selectedJobs.size, canvasser?.jobs_details?.length]);

  if (!isModalOpen) return null;

  return (
    <div className={classNames('relative', className)}>
      <input
        type="checkbox"
        id={modalName}
        className="modal-toggle"
        checked={isModalOpen}
        onChange={() => {}}
      />

      <ModalContainer
        isModalOpen={isModalOpen}
        modalName={modalName}
        variant={variant}
        onClose={onClose}
        shouldCloseOnBackgroundClick>
        <div className="flex h-full flex-col">
          {/* Header */}
          <ModalHeader
            modalName={modalName}
            closeAction="url"
            icon={<CanvassersIconSvg className="h-5 w-5" />}
            title={
              isLoading ? 'Loading...' : canvasser?.display_name || 'Canvasser Details'
            }
          />

          {/* Loading State */}
          {isLoading && (
            <div className="flex flex-1 items-center justify-center">
              <span className="loading loading-spinner loading-lg" />
            </div>
          )}

          {/* Error State */}
          {isError && (
            <div className="flex flex-1 flex-col items-center justify-center text-center">
              <div className="text-red-600">Failed to load canvasser details</div>
              <button onClick={onClose} className="btn btn-primary btn-sm mt-4">
                Close
              </button>
            </div>
          )}

          {/* Content */}
          {!isLoading && !isError && canvasser && (
            <div className="flex flex-1 overflow-hidden">
              {/* Main Panel */}
              <div className="flex flex-1 flex-col overflow-y-auto p-6 pb-24">
                {/* General Information Section */}
                <div className="mb-6 grid grid-cols-[auto_1fr_3fr] gap-x-4 gap-y-4">
                  {/* Status */}
                  <StatusSvg className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-500">
                    Status
                  </span>
                  <div className="flex items-center gap-2">
                    {getStatusIcon(canvasser.status)}
                    <span className="text-sm text-gray-900 dark:text-gray-300">
                      {canvasser.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  {/* Email */}
                  <EnvelopeIcon className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-500">
                    Email
                  </span>
                  <span className="text-sm text-gray-900 dark:text-gray-300">
                    {canvasser.display_email}
                  </span>

                  {/* Last Sign In */}
                  <KeyboardIcon className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-500">
                    Last sign in
                  </span>
                  <span className="text-sm text-gray-900 dark:text-gray-300">
                    {canvasser.last_sign_in
                      ? formatRelativeTime(canvasser.last_sign_in)
                      : 'Never'}
                  </span>
                </div>

                {/* Conversations Section */}
                <div className="mb-6">
                  <DetailsCard
                    icon={<CommentIcon className="h-5 w-5" />}
                    title="Conversations">
                    <div className="grid grid-cols-2 gap-0">
                      <div className="border-r border-gray-200 p-4 text-left dark:border-gray-800">
                        <div className="text-sm text-gray-500">Started Today</div>
                        <div className="text-3xl font-bold text-gray-900 dark:text-gray-300">
                          {canvasser.conversation_stats.started.toLocaleString()}
                        </div>
                      </div>
                      <div className="p-4 text-left">
                        <div className="text-sm text-gray-500">Needs Reply</div>
                        <div className="text-3xl font-bold text-gray-900 dark:text-gray-300">
                          {(
                            canvasser.conversation_stats.needs_reply ?? 0
                          ).toLocaleString()}
                        </div>
                      </div>
                      <div className="border-t border-r border-gray-200 p-4 text-left dark:border-gray-800">
                        <div className="text-sm text-gray-500">Replied Today</div>
                        <div className="text-3xl font-bold text-gray-900 dark:text-gray-300">
                          {canvasser.conversation_stats.replied.toLocaleString()}
                        </div>
                      </div>
                      <div className="border-t border-gray-200 p-4 text-left dark:border-gray-800">
                        <div className="text-sm text-gray-500">Total Messages Today</div>
                        <div className="text-3xl font-bold text-gray-900 dark:text-gray-300">
                          {(
                            canvasser.conversation_stats.started +
                            canvasser.conversation_stats.replied
                          ).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </DetailsCard>
                </div>

                {/* Jobs Section */}
                <div>
                  <DetailsCard
                    icon={<JobsIcon className="h-5 w-5" />}
                    title={`Jobs (${canvasser.jobs_details?.length || 0})`}
                    editButton={
                      canvasser.jobs_details && canvasser.jobs_details.length > 0 ? (
                        <button
                          onClick={handleAssignToJob}
                          className="btn btn-outline btn-sm">
                          <PlusIcon />
                          <span className={classNames(inter500.className, 'text-sm')}>
                            Assign
                          </span>
                        </button>
                      ) : undefined
                    }>
                    {canvasser.jobs_details && canvasser.jobs_details.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="table w-full">
                          <thead>
                            <tr>
                              <th className="w-12">
                                <input
                                  type="checkbox"
                                  className="checkbox-primary checkbox checkbox-sm"
                                  checked={selectAllJobs}
                                  ref={(el) => {
                                    if (el)
                                      el.indeterminate = isSelectAllJobsIndeterminate;
                                  }}
                                  onChange={(e) => handleSelectAllJobs(e.target.checked)}
                                />
                              </th>
                              <th>Name</th>
                              <th>Canvassers</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {canvasser.jobs_details.map((job) => (
                              <tr key={job.id} className="hover">
                                <td>
                                  <input
                                    type="checkbox"
                                    className="checkbox-primary checkbox checkbox-sm"
                                    checked={selectedJobs.has(job.id)}
                                    onChange={(e) =>
                                      handleSelectJob(job.id, e.target.checked)
                                    }
                                  />
                                </td>
                                <td>{job.name}</td>
                                <td>{job.numOfAgents}</td>
                                <td>
                                  <div className="flex items-center gap-2">
                                    {getJobStatusIcon(job.status)}
                                    <span>
                                      {job.status.charAt(0).toUpperCase() +
                                        job.status.slice(1)}
                                    </span>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>

                        {/* Bulk Actions */}
                        {selectedJobs.size > 0 && (
                          <div className="dark:border-base-200 dark:bg-base-200 rounded-lg border-t border-gray-200 bg-gray-50 p-4">
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-gray-600 dark:text-gray-400">
                                {selectedJobs.size} job
                                {selectedJobs.size === 1 ? '' : 's'} selected
                              </span>
                              <div className="flex gap-2">
                                <button className="btn btn-outline btn-sm">
                                  Unassign Selected
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="py-8 text-center text-gray-500">
                        <div className="mb-2 text-sm">No job assignments</div>
                        <button
                          onClick={handleAssignToJob}
                          className="btn btn-primary btn-sm">
                          Assign to Job
                        </button>
                      </div>
                    )}
                  </DetailsCard>
                </div>
              </div>
            </div>
          )}

          {/* Modal Actions Container */}
          {!isLoading && !isError && canvasser && (
            <ModalActionsContainer>
              <BulkActionButton
                icon={<KeyboardIcon className="h-4 w-4" />}
                label="Reset Password"
                onClick={handleResetPassword}
                className={resetPasswordMutation.isPending ? 'opacity-50' : ''}
              />
              <BulkActionButton
                icon={<TrashIcon className="h-4 w-4" />}
                label="Delete"
                onClick={handleDeleteCanvasser}
                className="text-red-600 hover:bg-red-50"
              />
            </ModalActionsContainer>
          )}
        </div>
      </ModalContainer>
    </div>
  );
};

export default CanvasserDetailsModal;
